import { useMemo } from "react";
import { cn } from "../lib/utils";
import { StatusChip } from "../components/ui/StatusChip";
import { OwnerTag } from "../components/ui/OwnerTag";
import { PriorityDot } from "../components/ui/PriorityDot";
import {
  IconUsers,
  IconVideo,
  IconRocket,
  IconCircleCoin,
  IconRoadmap,
} from "nucleo-glass";
import { NucleoIcon } from "../components/ui/NucleoIcon";
import type { ReactNode } from "react";
import type { Card } from "../data/types";
import type { KpiTotals } from "../lib/boardUtils";
import { getTaskMeta, sortCardsByExecution } from "../lib/taskMeta";

interface PipelinePageProps {
  cards: Card[];
  kpiTotals: KpiTotals;
  onSelectCard: (cardId: string) => void;
}

const sequenceSteps: Array<{ key: string; label: string }> = [
  { key: "D0", label: "D0" },
  { key: "D+2", label: "D+2" },
  { key: "D+5", label: "D+5" },
  { key: "D+10", label: "D+10" },
];

const funnelSteps: Array<{ label: string; value: (totals: KpiTotals, waitlist: number) => number; icon: ReactNode }> = [
  { label: "Leads", value: (_totals, waitlist) => waitlist, icon: <NucleoIcon icon={<IconUsers size={20} />} /> },
  { label: "Demos", value: (totals) => totals.demosBooked, icon: <NucleoIcon icon={<IconVideo size={20} />} /> },
  { label: "Trials", value: () => 0, icon: <NucleoIcon icon={<IconRocket size={20} />} /> },
  { label: "Cash Collected", value: (totals) => totals.newMrr, icon: <NucleoIcon icon={<IconCircleCoin size={20} />} /> },
];

function getSegment(card: Card): "waitlist" | "warm" | "alesso" | "reactivation" | "other" {
  const tags = card.tags.map((tag) => tag.toLowerCase());
  if (tags.includes("segment:waitlist")) return "waitlist";
  if (tags.includes("segment:warm")) return "warm";
  if (tags.includes("segment:alesso")) return "alesso";
  if (tags.includes("segment:reactivation")) return "reactivation";

  const title = card.title.toLowerCase();
  if (title.includes("waitlist")) return "waitlist";
  if (title.includes("warm")) return "warm";
  if (title.includes("alesso")) return "alesso";
  if (title.includes("reactivation")) return "reactivation";
  return "other";
}

export function PipelinePage({ cards, kpiTotals, onSelectCard }: PipelinePageProps) {
  const pipelineCards = useMemo(() => {
    return sortCardsByExecution(cards.filter((card) => card.laneId === "commercial_pipeline"));
  }, [cards]);

  const setupCards = useMemo(() => {
    return pipelineCards.filter((c) => getTaskMeta(c).taskType === "one_off" || getTaskMeta(c).taskType === "unknown");
  }, [pipelineCards]);

  const executionCards = useMemo(() => {
    return pipelineCards.filter((c) => getTaskMeta(c).taskType === "recurring_daily");
  }, [pipelineCards]);

  const waitlistCards = pipelineCards.filter((card) => getSegment(card) === "waitlist");
  const warmCards = pipelineCards.filter((card) => getSegment(card) === "warm");
  const alessoCards = pipelineCards.filter((card) => getSegment(card) === "alesso");
  const reactivationCards = pipelineCards.filter((card) => getSegment(card) === "reactivation");

  const sequenceCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const step of sequenceSteps) counts.set(step.key, 0);

    for (const card of pipelineCards) {
      const sequence = getTaskMeta(card).sequence;
      if (!sequence) continue;
      if (!counts.has(sequence)) continue;
      counts.set(sequence, (counts.get(sequence) ?? 0) + 1);
    }

    return counts;
  }, [pipelineCards]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full space-y-[var(--spacing-6)] px-[var(--spacing-4)] py-[var(--spacing-6)]">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-[var(--spacing-3)]">
          {funnelSteps.map((step, idx) => (
            <div
              key={step.label}
              className="rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)] p-[var(--spacing-4)] text-center animate-stagger-in"
              style={{ animationDelay: `${idx * 40}ms` }}
            >
              <div className="flex justify-center mb-[var(--spacing-2)] text-[var(--color-text-muted)]">{step.icon}</div>
              <span className="block text-[14px] text-[var(--color-text-muted)] mb-[var(--spacing-1)]">{step.label}</span>
              <span className="block text-[14px] font-semibold tabular-nums text-[var(--color-text-primary)]">
                {step.label === "Cash Collected" ? `€${step.value(kpiTotals, waitlistCards.length)}` : step.value(kpiTotals, waitlistCards.length)}
              </span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-[var(--spacing-3)]">
          <SegmentCard label="Waitlist" count={waitlistCards.length} />
          <SegmentCard label="Warm" count={warmCards.length} />
          <SegmentCard label="Alesso" count={alessoCards.length} />
          <SegmentCard label="Reactivation" count={reactivationCards.length} />
        </div>

        <div className="rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)] overflow-hidden">
          <div className="px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)] flex items-center gap-[var(--spacing-2)]">
            <NucleoIcon icon={<IconRoadmap size={16} />} className="text-[var(--color-text-muted)]" />
            <span className="text-[14px] font-semibold text-[var(--color-text-primary)]">Sequence Coverage</span>
            <span className="ml-auto text-[12px] text-[var(--color-text-muted)]">D0 / D+2 / D+5 / D+10</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[var(--color-border-stroke)]">
            {sequenceSteps.map((step) => (
              <div key={step.key} className="px-[var(--spacing-4)] py-[var(--spacing-3)]">
                <span className="text-[11px] uppercase tracking-[0.05em] text-[var(--color-text-muted)]">{step.label}</span>
                <div className="text-[20px] font-semibold tabular-nums text-[var(--color-text-primary)]">{sequenceCounts.get(step.key) ?? 0}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Daily Pipeline Execution */}
        {executionCards.length > 0 && (
          <div className="rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)] overflow-hidden">
            <div className="px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)] flex items-center gap-[var(--spacing-2)]">
              <NucleoIcon icon={<IconRoadmap size={16} />} className="text-[var(--color-accent-300)]" />
              <span className="text-[14px] font-semibold text-[var(--color-text-primary)]">Daily Pipeline Execution</span>
              <span className="text-[12px] text-[var(--color-text-muted)] ml-auto">{executionCards.filter((c) => c.status === "done").length}/{executionCards.length} done</span>
            </div>
            <div className="divide-y divide-[var(--color-border-stroke)]">
              {executionCards.map((card) => {
                const meta = getTaskMeta(card);
                const segment = getSegment(card);
                return (
                  <button
                    key={card.id}
                    type="button"
                    onClick={() => onSelectCard(card.id)}
                    className={cn(
                      "w-full flex items-center gap-[var(--spacing-3)] px-[var(--spacing-4)] py-[var(--spacing-2)]",
                      "text-left hover:bg-[var(--color-bg-hover)] cursor-pointer transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-focus)]",
                      card.status === "done" && "opacity-50",
                    )}
                  >
                    <PriorityDot priority={card.priority} />
                    <span className={cn("flex-1 text-[14px] truncate", card.status === "done" ? "text-[var(--color-text-muted)] line-through" : "text-[var(--color-text-primary)]")}>{card.title}</span>
                    <span className="text-[11px] text-[var(--color-text-muted)] capitalize shrink-0">{segment}</span>
                    {meta.executionWindow && <span className="text-[11px] text-[var(--color-text-muted)] shrink-0">{meta.executionWindow.replace("_CET", " CET")}</span>}
                    <OwnerTag owner={card.owner} />
                    <StatusChip status={card.status} />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Setup & Infrastructure (one-off) */}
        <div className="rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)] overflow-hidden">
          <div className="px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)] flex items-center gap-[var(--spacing-2)]">
            <NucleoIcon icon={<IconRoadmap size={16} />} className="text-[var(--color-text-muted)]" />
            <span className="text-[14px] font-semibold text-[var(--color-text-primary)]">Setup &amp; Infrastructure</span>
            <span className="text-[12px] text-[var(--color-text-muted)] ml-auto">{setupCards.length} tasks</span>
          </div>
          <table className="w-full text-[14px]">
            <thead>
              <tr className="border-b border-[var(--color-border-stroke)]">
                <th className="text-left font-semibold text-[var(--color-text-muted)] px-[var(--spacing-4)] py-[var(--spacing-2)]">Task</th>
                <th className="text-left font-semibold text-[var(--color-text-muted)] px-[var(--spacing-4)] py-[var(--spacing-2)] w-[110px]">Segment</th>
                <th className="text-left font-semibold text-[var(--color-text-muted)] px-[var(--spacing-4)] py-[var(--spacing-2)] w-[80px]">Owner</th>
                <th className="text-left font-semibold text-[var(--color-text-muted)] px-[var(--spacing-4)] py-[var(--spacing-2)] w-[100px]">Status</th>
                <th className="text-left font-semibold text-[var(--color-text-muted)] px-[var(--spacing-4)] py-[var(--spacing-2)] w-[160px]">Sequence</th>
              </tr>
            </thead>
            <tbody>
              {setupCards.map((card) => {
                const meta = getTaskMeta(card);
                const segment = getSegment(card);
                return (
                  <tr
                    key={card.id}
                    onClick={() => onSelectCard(card.id)}
                    className="border-b border-[var(--color-border-stroke)] last:border-b-0 hover:bg-[var(--color-bg-hover)] cursor-pointer transition-colors"
                  >
                    <td className="px-[var(--spacing-4)] py-[var(--spacing-2)]">
                      <div className="flex items-center gap-[var(--spacing-2)]">
                        <PriorityDot priority={card.priority} />
                        <span className="text-[var(--color-text-primary)] truncate">{card.title}</span>
                      </div>
                    </td>
                    <td className="px-[var(--spacing-4)] py-[var(--spacing-2)] capitalize text-[var(--color-text-muted)]">{segment}</td>
                    <td className="px-[var(--spacing-4)] py-[var(--spacing-2)]"><OwnerTag owner={card.owner} /></td>
                    <td className="px-[var(--spacing-4)] py-[var(--spacing-2)]"><StatusChip status={card.status} /></td>
                    <td className="px-[var(--spacing-4)] py-[var(--spacing-2)] text-[var(--color-text-muted)]">
                      <div className="flex items-center gap-[var(--spacing-2)]">
                        <span>{meta.sequence ?? "—"}</span>
                        {meta.executionWindow && <span className="text-[11px]">{meta.executionWindow.replace("_CET", " CET")}</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SegmentCard({ label, count }: { label: string; count: number }) {
  return (
    <div className={cn(
      "rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)]",
      "px-[var(--spacing-4)] py-[var(--spacing-3)]",
    )}>
      <span className="text-[11px] uppercase tracking-[0.05em] text-[var(--color-text-muted)]">{label}</span>
      <div className="text-[20px] font-semibold tabular-nums text-[var(--color-text-primary)]">{count}</div>
    </div>
  );
}
